import { useEffect, useState } from 'react';
import { SongTable } from '@/features/library';
import { DropZone, useImportStore } from '@/features/upload';
import { YOUTUBE_ENABLED, YouTubeLinkForm } from '@/features/youtube';
import { deleteSong, listSongs } from '@/storage/songsRepo';
import type { Song } from '@/storage/types';
import { useScreenStore } from '@/state/screenStore';
import styles from './LibraryPage.module.css';

export function LibraryPage() {
  const { showImport, showPlayer } = useScreenStore();
  const startImport = useImportStore((s) => s.start);
  const [songs, setSongs] = useState<Song[] | null>(null);

  useEffect(() => {
    let alive = true;
    void listSongs().then((list) => {
      if (alive) setSongs(list);
    });
    return () => {
      alive = false;
    };
  }, []);

  const [makingDemo, setMakingDemo] = useState(false);
  const playDemo = async () => {
    setMakingDemo(true);
    // Loaded on demand: the synthesizer is only needed for the demo.
    const { demoSongFile } = await import('@/core/synth/demo');
    // Let the button show "Making the demo…" before the synthesis blocks for a moment.
    await new Promise((r) => setTimeout(r, 30));
    onFile(demoSongFile());
  };

  const onFile = (file: File) => {
    void startImport(file);
    showImport();
  };

  const onDelete = async (song: Song) => {
    await deleteSong(song.id);
    setSongs(await listSongs());
  };

  return (
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroMain}>
          <h1 className={styles.title}>
            Drop a song. <span className={styles.titleAccent}>See the chords.</span>
          </h1>
          <p className={styles.lead}>
            Runs in your browser. Nothing is uploaded, no account needed.
          </p>
          <DropZone onFile={onFile} />
          {YOUTUBE_ENABLED && <YouTubeLinkForm />}
          <p className={styles.demo}>
            No file handy?{' '}
            <button
              type="button"
              className={styles.demoButton}
              disabled={makingDemo}
              onClick={() => void playDemo()}
            >
              {makingDemo ? 'Making the demo…' : 'Try a demo song'}
            </button>{' '}
            <span className={styles.muted}>(synthesized on your device, about a minute long)</span>
          </p>
        </div>

        <aside className={styles.aside}>
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>How it stays private</h2>
            <ol className={styles.steps}>
              <li>
                <span className={styles.stepNumber}>1</span>
                <span>
                  <b>Decoded in your browser.</b>{' '}
                  <span className={styles.muted}>
                    The file is read locally — nothing is sent anywhere.
                  </span>
                </span>
              </li>
              <li>
                <span className={styles.stepNumber}>2</span>
                <span>
                  <b>Analyzed on your device.</b>{' '}
                  <span className={styles.muted}>Beats, chords and key, usually in seconds.</span>
                </span>
              </li>
              <li>
                <span className={styles.stepNumber}>3</span>
                <span>
                  <b>Nothing is saved.</b>{' '}
                  <span className={styles.muted}>
                    Songs stay in this tab until you close or reload it. Only your preferences are
                    kept.
                  </span>
                </span>
              </li>
            </ol>
          </div>
        </aside>
      </section>

      {songs && (
        <SongTable
          songs={songs}
          onOpen={(song) => {
            showPlayer(song.id);
          }}
          onDelete={(song) => void onDelete(song)}
        />
      )}

      <footer className={styles.footer}>
        Songs, chords and audio are kept only while this tab is open. Press ? in the player for
        keyboard shortcuts.
      </footer>
    </main>
  );
}
